import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { WorkOrderServiceType, WorkOrderServiceTypeLabels, WorkOrderSource, WorkOrderSourceLabels, WorkOrderPriority } from '../../../enums/work-order.enum';
import { EditWorkOrderFormData, WorkshopOption, SourceOption } from '../../../models/work-order.models';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { WorkshopFilterService } from '../../../../../_shared/services/workshop-filter.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { WorkOrdersOverviewService } from '../../../services/work-orders-overview.service';
import { TechnicianService } from '../../../services/technician.service';
import { UpdateWorkOrderEditRequest } from '../../../models/work-orders-overview.models';
import { WorkOrderTechnician } from '../../../models/technician.model';
import { TechnicianMultiSelectDropdownComponent } from '../../_shared/technician-multi-select-dropdown/technician-multi-select-dropdown.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-edit-work-order-modal',
  standalone: true,
  imports: [NgbModule, CommonModule, FormsModule, FlatpickrDatePickerComponent, TechnicianMultiSelectDropdownComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './edit-work-order-modal.component.html',
  styleUrl: './edit-work-order-modal.component.scss'
})
export class EditWorkOrderModalComponent implements OnInit, OnDestroy {
  @Input() workOrderId: number | null = null;
  workOrderData: EditWorkOrderFormData | null = null;
  workshops: WorkshopOption[] = [];
  @Input() technicians: WorkOrderTechnician[] = [];

  private authService = inject(AuthService);
  private workshopFilterService = inject(WorkshopFilterService);
  private technicianService = inject(TechnicianService);
  private workOrdersService = inject(WorkOrdersOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancelSubject = new Subject<void>();

  currencySymbol: string = '';

  formData: EditWorkOrderFormData = {
    workOrderId: 0,
    workOrderIdDisplay: '',
    assetId: null,
    assetName: '',
    source: null,
    serviceType: null,
    technicianId: null,
    technicianIds: [],
    scheduledDate: null,
    workshopId: null,
    workType: '',
    reason: '',
    estimatedCost: null,
    estimatedTime: null,
    enableEmailAlerts: false,
    priority: WorkOrderPriority.Low
  };

  scheduledDateString: string = '';
  isLoading: boolean = false;
  isWorkOrderLoading: boolean = false;
  isTechniciansLoading: boolean = false;
  isWorkshopsLoading: boolean = false;
  scheduleDateTouched: boolean = false;
  timeSuffixLeft: number = 24;
  private initialFormData: EditWorkOrderFormData | null = null;
  private initialScheduledDateString: string = '';

  sourceDropdownOpen: boolean = false;
  serviceTypeDropdownOpen: boolean = false;
  workshopDropdownOpen: boolean = false;

  sources: SourceOption[] = [
    { id: WorkOrderSource.OEM, name: WorkOrderSourceLabels[WorkOrderSource.OEM] },
    { id: WorkOrderSource.Custom, name: WorkOrderSourceLabels[WorkOrderSource.Custom] },
    { id: WorkOrderSource.CanBus, name: WorkOrderSourceLabels[WorkOrderSource.CanBus] },
    { id: WorkOrderSource.DriverApp, name: WorkOrderSourceLabels[WorkOrderSource.DriverApp] },
    { id: WorkOrderSource.ComplianceApp, name: WorkOrderSourceLabels[WorkOrderSource.ComplianceApp] },
    { id: WorkOrderSource.System, name: WorkOrderSourceLabels[WorkOrderSource.System] }
  ];

  serviceTypes = [
    { id: WorkOrderServiceType.MinorService, name: WorkOrderServiceTypeLabels[WorkOrderServiceType.MinorService] },
    { id: WorkOrderServiceType.IntermediateService, name: WorkOrderServiceTypeLabels[WorkOrderServiceType.IntermediateService] },
    { id: WorkOrderServiceType.MajorService, name: WorkOrderServiceTypeLabels[WorkOrderServiceType.MajorService] }
  ];

  constructor(private modalInstance: NgbActiveModal) { }

  ngOnInit(): void {
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);

    void this.initializeFormAsync();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  get workOrderIdString(): string {
    return this.formData.workOrderIdDisplay || `WO-${this.formData.workOrderId.toString().padStart(6, '0')}`;
  }

  get selectedAssetName(): string {
    return this.formData.assetName || '';
  }

  get selectedServiceTypeName(): string {
    if (this.formData.serviceType === null) return '';
    const key = WorkOrderServiceTypeLabels[this.formData.serviceType];
    return key ? this.transloco.translate(key) : '';
  }

  get selectedWorkshopName(): string {
    return this.workshops.find(w => w.id === this.formData.workshopId)?.name || '';
  }

  get selectedSourceName(): string {
    if (this.formData.source === null) return '';
    const key = WorkOrderSourceLabels[this.formData.source];
    return key ? this.transloco.translate(key) : '';
  }

  get isFormValid(): boolean {
    return (
      this.formData.workOrderId !== 0 &&
      this.formData.assetId !== null &&
      this.formData.source !== null &&
      this.formData.serviceType !== null &&
      this.formData.technicianIds.length > 0 &&
      this.scheduledDateString !== '' &&
      this.hasValidWorkshopSelection
    );
  }

  get hasValidWorkshopSelection(): boolean {
    if (this.formData.workshopId === null) {
      return false;
    }

    return this.workshops.some(workshop => workshop.id === this.formData.workshopId);
  }

  get isInputLoading(): boolean {
    return this.isWorkOrderLoading || this.isTechniciansLoading || this.isWorkshopsLoading;
  }

  get sourceDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingSource');
    return this.selectedSourceName || '';
  }

  get serviceTypeDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingServiceType');
    return this.selectedServiceTypeName || this.transloco.translate('workOrder.modals.shared.selectServiceType');
  }

  get workshopDisplayText(): string {
    if (this.isWorkOrderLoading || this.isWorkshopsLoading) return this.transloco.translate('workOrder.modals.shared.loadingWorkshop');
    return this.selectedWorkshopName || this.transloco.translate('workOrder.modals.shared.selectWorkshop');
  }

  get maintenanceReasonDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingMaintenanceReason');
    return this.formData.localizedReason || this.formData.reason;
  }

  get hasChanges(): boolean {
    if (!this.initialFormData) {
      return false;
    }

    return (
      this.formData.source !== this.initialFormData.source ||
      this.formData.serviceType !== this.initialFormData.serviceType ||
      this.formData.workshopId !== this.initialFormData.workshopId ||
      !this.haveSameTechnicianIds(this.formData.technicianIds, this.initialFormData.technicianIds) ||
      this.formData.estimatedCost !== this.initialFormData.estimatedCost ||
      this.formData.estimatedTime !== this.initialFormData.estimatedTime ||
      this.formData.enableEmailAlerts !== this.initialFormData.enableEmailAlerts ||
      this.formData.priority !== this.initialFormData.priority ||
      this.scheduledDateString !== this.initialScheduledDateString
    );
  }

  toggleSourceDropdown(): void {
    if (this.isInputLoading) return;
    this.sourceDropdownOpen = !this.sourceDropdownOpen;
    this.closeOtherDropdowns('source');
  }

  toggleServiceTypeDropdown(): void {
    if (this.isInputLoading) return;
    this.serviceTypeDropdownOpen = !this.serviceTypeDropdownOpen;
    this.closeOtherDropdowns('serviceType');
  }

  toggleWorkshopDropdown(): void {
    if (this.isInputLoading) return;
    this.workshopDropdownOpen = !this.workshopDropdownOpen;
    this.closeOtherDropdowns('workshop');
  }

  private closeOtherDropdowns(except: string): void {
    if (except !== 'source') this.sourceDropdownOpen = false;
    if (except !== 'serviceType') this.serviceTypeDropdownOpen = false;
    if (except !== 'workshop') this.workshopDropdownOpen = false;
  }

  selectSource(source: SourceOption): void {
    this.formData.source = source.id;
    this.sourceDropdownOpen = false;
  }

  selectServiceType(serviceType: { id: WorkOrderServiceType; name: string }): void {
    this.formData.serviceType = serviceType.id;
    this.serviceTypeDropdownOpen = false;

    // Auto-set priority based on service type
    switch (serviceType.id) {
      case WorkOrderServiceType.MajorService:
        this.formData.priority = WorkOrderPriority.High;
        break;
      case WorkOrderServiceType.IntermediateService:
        this.formData.priority = WorkOrderPriority.Medium;
        break;
      case WorkOrderServiceType.MinorService:
        this.formData.priority = WorkOrderPriority.Low;
        break;
    }
  }

  selectWorkshop(workshop: WorkshopOption): void {
    this.formData.workshopId = workshop.id;
    this.workshopDropdownOpen = false;
  }

  onTechnicianIdsChange(technicianIds: number[]): void {
    this.formData.technicianIds = technicianIds;
  }

  onScheduledDateChange(value: string | undefined): void {
    this.scheduledDateString = value ?? '';
    this.scheduleDateTouched = true;
  }

  closeAllDropdowns(): void {
    this.sourceDropdownOpen = false;
    this.serviceTypeDropdownOpen = false;
    this.workshopDropdownOpen = false;
  }

  onTimeInputChange(): void {
    const value = this.formData.estimatedTime;
    if (value === null || value === undefined) {
      this.timeSuffixLeft = 18;
    } else {
      const valueStr = value.toString();
      const charWidth = 7;
      this.timeSuffixLeft = 14 + (valueStr.length * charWidth) + 6;
    }
  }

  get hasEstimatedTimeValue(): boolean {
    const value = this.formData.estimatedTime as unknown;
    if (value === null || value === undefined) {
      return false;
    }

    if (String(value).trim() === '') {
      return false;
    }

    return !Number.isNaN(Number(value));
  }

  async saveWorkOrder(): Promise<void> {
    if (!this.isFormValid) return;

    const scheduledDate = this.parseScheduledDate(this.scheduledDateString);
    if (!scheduledDate) {
      this.scheduleDateTouched = true;
      return;
    }

    this.isLoading = true;

    try {
      const request: UpdateWorkOrderEditRequest = {
        workOrderId: this.formData.workOrderId,
        workType: this.formData.workType?.trim() ?? '',
        serviceType: this.formData.serviceType!,
        scheduledDate,
        workshopId: this.formData.workshopId!,
        enableEmailAlert: this.formData.enableEmailAlerts,
        comment: undefined,
        priority: this.formData.priority,
        technicianIds: this.formData.technicianIds
      };

      await this.workOrdersService.updateWorkOrderEditAsync(request, this.cancelSubject);

      this.toastr.success(this.transloco.translate('workOrder.toasts.updated'));
      this.modalInstance.close(request);
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }

  private formatDateForPicker(date: Date): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');
    // Return format matching Flatpickr's Y-m-d H:i format.
    return `${year}-${month}-${day} ${hour}:${minute}`;
  }

  private parseScheduledDate(value: string): Date | null {
    if (!value) return null;

    const [datePart, timePart = '00:00'] = value.trim().split(' ');
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const [hourStr = '00', minuteStr = '00'] = timePart.split(':');

    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    const hour = Number(hourStr);
    const minute = Number(minuteStr);

    if (
      Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day) ||
      Number.isNaN(hour) || Number.isNaN(minute)
    ) {
      return null;
    }

    return new Date(year, month - 1, day, hour, minute, 0, 0);
  }

  private async initializeFormAsync(): Promise<void> {
    if (this.workOrderId !== null) {
      await this.loadWorkOrderById();
    } else if (this.workOrderData) {
      this.formData = {
        ...this.workOrderData,
        technicianIds: this.workOrderData.technicianIds?.length
          ? this.workOrderData.technicianIds
          : this.workOrderData.technicianId ? [this.workOrderData.technicianId] : []
      };
      if (this.formData.scheduledDate) {
        this.scheduledDateString = this.formatDateForPicker(this.formData.scheduledDate);
      }
      this.onTimeInputChange();
    }

    if (this.technicians.length === 0) {
      await this.loadTechnicians();
    }

    if (this.workshops.length === 0) {
      await this.loadWorkshops();
    }

    this.setInitialState();
  }

  private async loadWorkOrderById(): Promise<void> {
    if (this.workOrderId === null) {
      return;
    }

    this.isWorkOrderLoading = true;
    try {
      const response = await this.workOrdersService.getWorkOrderByIdAsync(this.workOrderId, this.cancelSubject);
      const workOrder = response.data;
      if (!workOrder) {
        return;
      }

      const assignedTechnicianIds = workOrder.assignedTechnicians?.map(technician => technician.technicianId) ?? [];

      this.formData = {
        workOrderId: workOrder.workOrderId,
        workOrderIdDisplay: workOrder.workOrderNumber,
        assetId: workOrder.assetId,
        assetName: workOrder.assetName,
        source: workOrder.source,
        serviceType: workOrder.serviceType,
        technicianId: workOrder.technicianId ?? null,
        technicianIds: assignedTechnicianIds.length
          ? assignedTechnicianIds
          : workOrder.technicianId ? [workOrder.technicianId] : [],
        scheduledDate: workOrder.scheduledDate ? new Date(workOrder.scheduledDate) : null,
        workshopId: workOrder.workshopId,
        workType: workOrder.workType ?? '',
        reason: workOrder.reason ?? '',
        localizedReason: workOrder.localizedReason ?? null,
        estimatedCost: workOrder.estimatedCost ?? null,
        estimatedTime: workOrder.estimatedTime ?? null,
        enableEmailAlerts: workOrder.emailAlertEnabled ?? false,
        priority: workOrder.priority
      };

      if (this.formData.scheduledDate) {
        this.scheduledDateString = this.formatDateForPicker(this.formData.scheduledDate);
      } else {
        this.scheduledDateString = '';
      }
      this.onTimeInputChange();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
      this.closeModal();
    } finally {
      this.isWorkOrderLoading = false;
    }
  }

  private setInitialState(): void {
    this.initialFormData = { ...this.formData, technicianIds: [...this.formData.technicianIds] };
    this.initialScheduledDateString = this.scheduledDateString;
  }

  private haveSameTechnicianIds(currentIds: number[], initialIds: number[]): boolean {
    if (currentIds.length !== initialIds.length) {
      return false;
    }

    const current = [...currentIds].sort((a, b) => a - b);
    const initial = [...initialIds].sort((a, b) => a - b);
    return current.every((id, index) => id === initial[index]);
  }

  private async loadWorkshops(): Promise<void> {
    this.isWorkshopsLoading = true;
    try {
      const response = await this.workshopFilterService.getWorkshopList([], this.cancelSubject);
      this.workshops = (response.data ?? []).map(workshop => ({
        id: workshop.id,
        name: workshop.name
      }));

      if (this.workshops.length > 0 && this.formData.workshopId === null) {
        this.formData.workshopId = this.workshops[0].id;
      }

      this.isWorkshopsLoading = false
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isWorkshopsLoading = false
      });
    }
  }

  private async loadTechnicians(): Promise<void> {
    try {
      this.isTechniciansLoading = true;
      const response = await this.technicianService.getTechniciansAsync([this.formData.workOrderId], this.cancelSubject);
      this.technicians = response.data ?? [];

      this.isTechniciansLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isTechniciansLoading = false
      });
    }
  }
}